import { useEffect, useRef, useState } from 'react';

import { topmostOverlay } from '@bookora/ui';

import {
  EDITING_RECENT_MS,
  OFFER_AUTO_COLLAPSE_MS,
  OFFER_SCROLL_COLLAPSE_PX,
  OVERLAY_POLL_MS,
} from './timing';

/** An IME composition (a Persian keyboard on a phone) is typing too. */
const INPUT_EVENTS = [
  'pointerdown',
  'keydown',
  'wheel',
  'input',
  'compositionstart',
  'compositionupdate',
] as const;

const EDITABLE =
  'input:not([type=checkbox]):not([type=radio]):not([type=button]):not([type=submit]):not([type=reset]),textarea,[contenteditable=""],[contenteditable="true"],[role=combobox]';

let lastKeyAt = 0;
let composing = false;
let tracking = false;

/** Installed once, lazily: when the last key went down, and whether an IME is composing. */
function trackTyping(): void {
  if (tracking || typeof document === 'undefined') return;
  tracking = true;
  document.addEventListener('keydown', () => (lastKeyAt = Date.now()), true);
  document.addEventListener('compositionstart', () => (composing = true), true);
  document.addEventListener('compositionend', () => (composing = false), true);
}

/**
 * True while the person is in the middle of entering something: the caret is
 * in an editable field AND (a key went down in the last 8 s, the field already
 * holds text, or an IME is composing). A 1.2 s pause with the caret in a
 * half-filled field is not «quiet» — nothing proactive lands mid-entry.
 */
export function isEditingNow(now = Date.now()): boolean {
  trackTyping();
  if (typeof document === 'undefined') return false;
  const el = document.activeElement;
  if (!(el instanceof HTMLElement) || !el.matches(EDITABLE)) return false;
  if (composing || now - lastKeyAt < EDITING_RECENT_MS) return true;
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) return el.value !== '';
  return (el.textContent ?? '') !== '';
}

/** The route's Suspense has settled: no lazy-route fallback in the main region. */
export function routeSettled(): boolean {
  return document.querySelector('#bk-main .bk-route-fallback') === null;
}

/**
 * The page is ready for something guidance shows on its own: the tab is
 * visible, no foreign overlay is open and the route has rendered. Shared by
 * the welcome and a deep-linked shell tour.
 */
export function conditionsHold(): boolean {
  return document.visibilityState === 'visible' && topmostOverlay() === null && routeSettled();
}

/** Test seam. */
export function resetTypingTracker(): void {
  lastKeyAt = 0;
  composing = false;
}

/**
 * Resolves `true` once the document has had `quietMs` without a pointerdown,
 * keydown, wheel, input or composition — the user is not in the middle of
 * something — or `false` after `maxMs`, or on abort. Nothing proactive
 * interrupts typing. With `notEditing`, a quiet moment with the caret still
 * in a half-filled field does not count either (help offers).
 */
export function waitForQuiet(
  quietMs: number,
  maxMs: number,
  signal?: AbortSignal,
  opts: { notEditing?: boolean } = {},
): Promise<boolean> {
  trackTyping();
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve(false);
      return;
    }
    let quietTimer: ReturnType<typeof setTimeout>;
    const finish = (value: boolean): void => {
      clearTimeout(quietTimer);
      clearTimeout(maxTimer);
      for (const type of INPUT_EVENTS) document.removeEventListener(type, onInput, true);
      signal?.removeEventListener('abort', onAbort);
      resolve(value);
    };
    const arm = (): void => {
      clearTimeout(quietTimer);
      quietTimer = setTimeout(() => {
        if (opts.notEditing && isEditingNow()) arm();
        else finish(true);
      }, quietMs);
    };
    const onInput = (): void => arm();
    const onAbort = (): void => finish(false);
    const maxTimer = setTimeout(() => finish(false), maxMs);
    for (const type of INPUT_EVENTS) document.addEventListener(type, onInput, true);
    signal?.addEventListener('abort', onAbort);
    arm();
  });
}

/**
 * Whether a foreign overlay (a Modal, a Drawer, an open Select or dropdown)
 * is on screen, polled every 250 ms while `active`: antd closes popups by
 * toggling classes on portal descendants, which a `childList` observer on
 * `body` never sees.
 */
export function useOverlayOpen(active: boolean): boolean {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!active) return;
    const check = (): void => setOpen(topmostOverlay() !== null);
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true });
    const poll = setInterval(check, OVERLAY_POLL_MS);
    return () => {
      observer.disconnect();
      clearInterval(poll);
    };
  }, [active]);
  return active && open;
}

/** The shell header's bottom edge: floating panels and sheets never sit under it. */
export function headerInsets(): { top: number; bottom: number } {
  const header = document.querySelector('.bk-shell__header');
  const bottom = header ? header.getBoundingClientRect().bottom : 0;
  return { top: Math.max(0, bottom), bottom: 0 };
}

/**
 * An offer the user did not answer goes away on its own: a pointerdown
 * outside it, typing outside it, scrolling more than 200 px, or 20 seconds.
 * The `OFFERED` already counted the impression; nothing else is recorded.
 */
export function useCollapseWhenIgnored(
  open: boolean,
  offerId: string,
  onCollapse: () => void,
): void {
  const collapseRef = useRef(onCollapse);
  useEffect(() => {
    collapseRef.current = onCollapse;
  }, [onCollapse]);
  useEffect(() => {
    if (!open) return;
    const inside = (target: EventTarget | null): boolean => {
      const offer = document.getElementById(offerId);
      const node = target instanceof Node ? target : null;
      if (!offer || !node) return false;
      // The trigger (the offer's previous sibling) counts as inside.
      return offer.contains(node) || Boolean(offer.previousElementSibling?.contains(node));
    };
    const collapse = (): void => collapseRef.current();
    const onPointer = (e: Event): void => {
      if (!inside(e.target)) collapse();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Tab' || e.key === 'Shift') return;
      if (!inside(e.target)) collapse();
    };
    const startY = window.scrollY;
    const onScroll = (): void => {
      if (Math.abs(window.scrollY - startY) > OFFER_SCROLL_COLLAPSE_PX) collapse();
    };
    const timer = setTimeout(collapse, OFFER_AUTO_COLLAPSE_MS);
    document.addEventListener('pointerdown', onPointer, true);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      clearTimeout(timer);
      document.removeEventListener('pointerdown', onPointer, true);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', onScroll);
    };
  }, [open, offerId]);
}
